import React, { useContext, useEffect } from 'react';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import Layout from '../Layout/index';
import MainWrapper from './MainWrapper';
import LogIn from '../LogIn';
import { getUser } from '../../services/auth';
import { AuthContext } from '../../context/auth';
import ScrollToTop from './ScrollToTop';
import { Dashboard } from '../Dashboard';
import { CampaignIndex } from '../Campaign';
import { FundRequestsIndex } from '../FundRequests';
import { DisbursementsIndex } from '../Disbursements';
import { TeamIndex } from '../Team';
import { FundraiserIndex } from '../Fundraisers/index';
import { HelpfulTools } from '../Campaign/components/HelpfulTools';
import { GetStartedIndex } from '../GetStarted';
import { ForgotPassword } from '../LogIn/ForgotPassword';
import { ResetPassword } from '../LogIn/ResetPassword';
import { PatientIndex } from '../Patient';
import { ProfileIndex } from '../Profile';
import { StartCampaignIndex } from '../StartCampaign';
import AdminNavBar from '../Admin/AdminNavBar';
import { MyIdIndex } from '../MyId';
import { CampaignContext } from '~/context/campaign';
import { MyIdContext } from '~/context/my-id';
import { checkRedemption } from '~/services/my-id';
import { fetchGrants } from '../../services/campaign';
import { GrantsContext } from '~/context/grants';
import { AdminContext } from '~/context/admin';

import TagManager from 'react-gtm-module';
const tagManagerArgs = {
    gtmId: 'GTM-TVKNFXL',
};
TagManager.initialize(tagManagerArgs);

const whitelistedRoutes = [
    '/login',
    '/get-started/create-password',
    '/get-started/create-password/confirmation',
    '/forgot-password',
    '/reset-password',
];

const Pages = () => {
    const my_id_panel = useContext(MyIdContext)[0].my_id_panel;
    const [{ campaign }] = useContext(CampaignContext);
    const location = useLocation();

    useEffect(() => {
        if (campaign) {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'page_view',
                    page: location.pathname,
                    user_role: campaign.role,
                    user_id: campaign.person?.id,
                    user_campaign: campaign.campaignDetail?.campaign_id,
                },
            });
        }
    }, [location]);

    return (
        <Switch>
            <Route exact path='/dashboard' component={Dashboard} />
            <Route path='/campaign' component={CampaignIndex} />
            <Route path='/team' component={TeamIndex} />
            <Route path='/patient' component={PatientIndex} />
            <Route path='/requests' component={FundRequestsIndex} />
            <Route path='/payees' component={FundRequestsIndex} />
            <Route path='/disbursements' component={DisbursementsIndex} />
            <Route path='/fundraisers' component={FundraiserIndex} />
            <Route path='/helpful-tools' component={HelpfulTools} />
            <Route path='/profile' component={ProfileIndex} />
            {my_id_panel && <Route path='/my-id' component={MyIdIndex} />}
        </Switch>
    );
};

const wrappedRoutes = () => (
    <div>
        <Layout />
        <div className='container__wrap'>
            <AdminNavBar />
            <Route path='/' component={Pages} />
        </div>
    </div>
);

const Router = () => {
    const [{ user: contextUser }, userDispatch] = useContext(AuthContext);
    const [{ campaign }] = useContext(CampaignContext);
    const [_myId, myIdDispatch] = useContext(MyIdContext);
    const [_grants, grantsDispatch] = useContext(GrantsContext);
    const [_admin, adminDispatch] = useContext(AdminContext);
    const token = localStorage.getItem('token');
    const userString = localStorage.getItem('user');
    const history = useHistory();

    // Handle campaignId from URL parameters for admin access
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const urlCampaignId = urlParams.get('campaignId');

        if (urlCampaignId) {
            adminDispatch({
                type: 'set_admin',
                isAdmin: true,
                campaignId: urlCampaignId,
            });
        }
    }, [adminDispatch]);

    const fetchUser = async () => {
        const resp = await getUser();
        if (!resp?.user?.id) {
            history.push('/login');
        } else {
            userDispatch({
                type: 'set_user',
                user: resp.user,
                person: resp.person,
            });
        }
    };

    useEffect(() => {


        // If user is not set in context but we have token and user data in localStorage
        if (!contextUser && token && userString) {
            console.log('🔄 Setting user from localStorage');
            try {
                const parsedUser = JSON.parse(userString);
                userDispatch({
                    type: 'set_user',
                    user: parsedUser,
                });
            } catch (error) {
                // If user data is corrupted, fetch fresh data
                console.error('Error parsing stored user data:', error);
                fetchUser();
            }
        } else if (!contextUser && token) {
            // Have token but no stored user data, fetch from server
            console.log('🔄 Fetching user from server');
            fetchUser();
        } else if (!contextUser && !token) {
            // No authentication data at all, redirect to login with next parameter
            // But only if we're not on a whitelisted route to avoid redirecting from public pages
            const currentPath = history.location.pathname;
            const isWhitelistedRoute = whitelistedRoutes.includes(currentPath);
            
            if (!isWhitelistedRoute && currentPath !== '/login') {
                const fullPath = history.location.pathname + history.location.search;
                console.log('🔀 Redirecting to login with next:', fullPath);
                history.push(`/login?next=${encodeURIComponent(fullPath)}`);
            } else {
                console.log('⚠️ On whitelisted route or login page, not redirecting');
            }
        }
    }, [contextUser, token, userString]);

    useEffect(() => {
        if (campaign) {
            checkRedemption(campaign?.campaignDetail?.campaign_id)
                .then((res) => {
                    myIdDispatch({
                        type: 'set-my-id',
                        payload: res,
                    });
                })
                .catch();
        }
    }, [campaign?.campaignDetail?.campaign_id]);

    const fetchGrant = async () => {
        if (campaign) {
            try {
                const ntaf = campaign.campaignDetail.ntaf_id;
                if (!ntaf) {
                    return;
                }
                const resp = await fetchGrants(ntaf);
                grantsDispatch({
                    type: 'set-grants',
                    payload: resp,
                });
            } catch (e) {
                console.log(e);
            }
        }
    };

    useEffect(() => {
        if (campaign?.campaignDetail?.ntaf_id) {
            fetchGrant();
        } else {
            grantsDispatch({
                type: 'set-grants',
                payload: [],
            });
        }
    }, [
        campaign?.campaignDetail?.ntaf_id,
        campaign?.campaignDetail?.campaign_id,
    ]);

    return (
        <MainWrapper>
            <>
                <ScrollToTop />
                <main>
                    <Switch>
                        <Route exact path='/' component={LogIn} />
                        <Route exact path='/login' component={LogIn} />
                        <Route
                            exact
                            path='/forgot-password'
                            component={ForgotPassword}
                        />
                        <Route
                            exact
                            path='/reset-password'
                            component={ResetPassword}
                        />
                        <Route
                            path='/get-started'
                            component={GetStartedIndex}
                        />
                        <Route
                            path='/start-campaign'
                            component={StartCampaignIndex}
                        />
                        <Route path='/' component={wrappedRoutes} />
                    </Switch>
                </main>
            </>
        </MainWrapper>
    );
};

export default Router;
